import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Uploads each file gesso's file drop reports, while the form is still
// open, and leaves a signed id behind in a hidden field for the submit
// to carry.
//
// The two are deliberately separate: gesso gathers files and knows
// nothing about storage, and this knows about storage and nothing about
// drag events.
export default class extends Controller {
  static targets = ["fields", "submit"]
  static values = { url: String }

  connect() {
    this.uploading = 0
    this.fields = new Map()
    this.failures = new Set()
    this.discarded = new WeakSet()
  }

  // The queue is the file drop's, and its indexes shift when a row is
  // removed, so the file itself is what ties a field to its upload.
  //
  // `progress` comes from the file drop and paints its own row, so how
  // far an upload has got is reported without this controller knowing
  // anything about that markup.
  added({ detail: { file, progress, fail } }) {
    this.#hold()

    const upload = new DirectUpload(file, this.urlValue, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) progress(event.loaded / event.total)
        })
      }
    })

    upload.create((error, blob) => {
      if (this.discarded.has(file)) return
      if (error) return this.#reportFailure(file, fail)

      progress(1)
      this.#appendField(file, blob.signed_id)
      this.#release()
    })
  }

  // Only a row the form came back with has no file of its own behind
  // it, and those are rendered in order, so only those go by position.
  removed({ detail: { index, file } }) {
    if (file.attached) return this.fieldsTarget.children[index]?.remove()

    const field = this.fields.get(file)

    if (field) return this.#removeField(file, field)
    if (this.failures.delete(file)) return this.#updateSubmit()

    this.#discard(file)
  }

  // Submitting while an upload is in flight would file a communication
  // missing the document, and so would submitting with one that never
  // arrived — so the button waits for both.
  #hold() {
    this.uploading += 1
    this.#updateSubmit()
  }

  #release() {
    this.uploading -= 1
    this.#updateSubmit()
  }

  #updateSubmit() {
    this.submitTarget.disabled =
      this.uploading > 0 || this.failures.size > 0
  }

  #removeField(file, field) {
    this.fields.delete(file)
    field.remove()
  }

  // The hold is let go here rather than in the callback, so the form
  // stops waiting on a document nobody wants.
  #discard(file) {
    this.discarded.add(file)
    this.#release()
  }

  #appendField(file, signedId) {
    const field = document.createElement("input")

    field.type = "hidden"
    field.name = this.fieldsTarget.dataset.fieldName
    field.value = signedId

    this.fields.set(file, field)
    this.fieldsTarget.append(field)
  }

  // The file drop owns the row that says what went wrong, so the
  // failure is handed back to it rather than drawn here. It is held
  // until the row goes, since nothing else says the document is missing.
  #reportFailure(file, fail) {
    this.failures.add(file)
    this.#release()
    fail()
  }
}
